// Plutus Realtime tools + instruction builder.
// These let the voice agent fetch the user's real data and do quick math by voice,
// instead of hallucinating. Executed client-side (has the user's session + risk profile).

import { supabase } from '@/integrations/supabase/client';
import type { RealtimeTool } from '@/utils/RealtimeAudio';

export const PLUTUS_TOOLS: RealtimeTool[] = [
  {
    type: 'function',
    name: 'get_my_stats',
    description:
      "Get the trader's own performance stats (TDI score, win rate, total decisions, executed trades, streak) over the last 30 days. Use when they ask about their performance, discipline, TDI, or win rate.",
    parameters: { type: 'object', properties: {}, required: [] },
  },
  {
    type: 'function',
    name: 'get_latest_analysis',
    description:
      "Get the trader's most recent Snap-Chart AI analysis (asset, verdict TRADE/WAIT/NO-TRADE, direction, key levels). Use when they ask 'what did the AI say', reference their last analysis, or ask about a chart they just analyzed.",
    parameters: {
      type: 'object',
      properties: {
        asset: { type: 'string', description: 'Optional asset symbol to filter by, e.g. XAUUSD, BTC.' },
      },
      required: [],
    },
  },
  {
    type: 'function',
    name: 'calc_position_size',
    description:
      "Calculate position size from the trader's configured risk amount given an entry and stop-loss price. Use when they ask 'how much can I size', 'what's my position size', or give an entry and stop.",
    parameters: {
      type: 'object',
      properties: {
        entry_price: { type: 'number', description: 'Planned entry price.' },
        stop_loss: { type: 'number', description: 'Planned stop-loss price.' },
      },
      required: ['entry_price', 'stop_loss'],
    },
  },
];

export interface PlutusToolContext {
  userId: string | null;
  riskAmount: number; // $ risked per trade from the user's risk profile
}

export async function executePlutusTool(
  name: string,
  args: any,
  ctx: PlutusToolContext
): Promise<Record<string, unknown>> {
  try {
    switch (name) {
      case 'get_my_stats': {
        if (!ctx.userId) return { error: 'No user session.' };
        const { data, error } = await supabase.rpc('get_journal_stats', { p_user_id: ctx.userId, p_days: 30 });
        if (error || !data) return { error: 'Stats unavailable right now.' };
        return {
          tdi_score: data.tdi_score ?? null,
          win_rate_pct: data.win_rate ?? null,
          total_decisions: data.total_decisions ?? 0,
          executed_trades: data.executed_trades ?? 0,
          current_streak: data.current_streak ?? null,
          window: 'last 30 days',
        };
      }

      case 'get_latest_analysis': {
        if (!ctx.userId) return { error: 'No user session.' };
        let q = supabase
          .from('journal_entries')
          .select('asset_symbol, market_type, direction, execution_permission, entry_price, stop_loss, take_profit_1, created_at')
          .eq('user_id', ctx.userId)
          .order('created_at', { ascending: false })
          .limit(1);
        if (args?.asset) q = q.ilike('asset_symbol', `%${String(args.asset).replace(/usd$/i, '')}%`);
        const { data, error } = await q;
        if (error || !data || data.length === 0) return { error: 'No recent analysis found.' };
        const e = data[0] as any;
        return {
          asset: e.asset_symbol,
          market: e.market_type,
          verdict: e.execution_permission || 'N/A',
          direction: e.direction || null,
          entry: e.entry_price ?? null,
          stop_loss: e.stop_loss ?? null,
          take_profit: e.take_profit_1 ?? null,
          when: e.created_at,
        };
      }

      case 'calc_position_size': {
        const entry = Number(args?.entry_price);
        const stop = Number(args?.stop_loss);
        if (!isFinite(entry) || !isFinite(stop) || entry === stop) {
          return { error: 'Need a valid entry and a different stop-loss price.' };
        }
        if (!ctx.riskAmount || ctx.riskAmount <= 0) {
          return { error: 'No risk amount configured. Set a risk profile first.' };
        }
        const riskPerUnit = Math.abs(entry - stop);
        const units = ctx.riskAmount / riskPerUnit;
        return {
          risk_amount_usd: Math.round(ctx.riskAmount),
          risk_per_unit: Number(riskPerUnit.toFixed(4)),
          position_size_units: Number(units.toFixed(2)),
          note: 'Units = risk amount / (|entry - stop|). Confirm contract/lot sizing for your instrument.',
        };
      }

      default:
        return { error: `Unknown tool: ${name}` };
    }
  } catch (err) {
    console.error('Tool execution error:', name, err);
    return { error: 'Tool failed to run.' };
  }
}

// Build the Plutus system instructions with the live context block embedded.
export function buildPlutusInstructions(contextBlock: string): string {
  return `You are Plutus, an institutional-grade trading risk officer speaking out loud with a trader.

PERSONALITY: calm, authoritative, supportive — a senior trading-desk mentor. Natural and concise,
never robotic or corporate. Short sentences. 1-2 key points. Reward discipline over frequency.

GROUNDING: You are given a live CONTEXT block with the trader's real risk profile, latest analysis,
live prices, TDI and stats. ALWAYS use it. NEVER invent numbers. If the user asks about their data
or wants a calculation, CALL A TOOL (get_my_stats, get_latest_analysis, calc_position_size) rather
than guessing. While a tool runs, keep talking naturally ("let me pull that up...").

VISION: The trader can share their CURRENT CHART with you — when an image appears in the
conversation, you can SEE it. Read the actual structure, levels, candles and any drawn entries/
stops on screen. Describe what is genuinely visible; prices on the chart are from the screenshot,
not live feeds. If they ask "what do you see?" or "look at this", ground your answer in the image.

DECISIONS: speak like a real desk — "I'd take it", "I'd wait", "Not this one". Explain the risk
before the opportunity. Discourage impulsive or revenge trades. If the risk profile is missing,
say you can't size risk until they set one.

LIVE CONTEXT:
${contextBlock}`;
}
